// RPG UI: 마을 HUD(보유 골드·가방 버튼·대화 안내) + NPC 창(대장간·상점·교관) + 가방 창(장비·상태·스킬).
// 2026-10-17 그래픽 개편 2차: 콘셉트 이미지 전체를 목표로 배치를 맞춘다(설계도 work/blueprint 노드 ui-bag-*, ui-npc-*).
// 가로 화면: 실제 게임 배경 위에 왼쪽 그림(카엘 입상·NPC 상반신)과 대사/능력치 상자, 오른쪽 패널, 위쪽 제목줄·골드.
// 세로 화면: 한 칸으로 쌓은 틀 창.
// 게임 상태는 읽기만 한다. 구매·장착·주력·닫기·복구는 GameCommand로만 보내고, 결과는 profile.last(seq)와 profile 값으로 되읽는다.
// 표시 데이터는 rpgModel.ts(순수 로직), 그림·등급은 rpgArt.ts가 만든다. DOM은 값이 바뀔 때만 다시 그린다(프레임마다 새 객체를 만들지 않는다).
import { RPG_WINDOW_EVENT } from './rpgWindowEvent';
import { npcDef, npcGreeting, townNpcAt, type NpcRole } from '../shared/rpg';
import { TOWN_GEAR_TOWNS } from '../shared/content/townShop';
import { setNearNpc } from '../render/env/npcFocus';
import { Btn, Scene, type GameCommand, type GameView, type InputSink } from '../shared/types';
import { gearTotalsOf } from '../shared/gearView';
import { SKILL_NODES } from '../shared/growth';
import { FONT, FONT_NUM, GOLD_TEXT, el, fmtNum, hudUrl, portraitImage } from './dom';
import {
  ResultGate, goldText, interactPrompt, makeRequestId, modeNotice, skillRows, statRows, tradeLocked, txMessage,
  type StatKey, type Tone, type TxMessage,
} from './rpgModel';
import { SLOT_KO, equippedCards } from './gearModel';
import { SLOT_ORDER } from '../shared/gearLedger';
import { SkillTreeUi } from './skillTree';
import { SkillDrawerUi } from './skillDrawer';
import { slotsOf } from './slotsModel';
import { drawStatPanel, statPanelKey as statKey } from './statPanel';
import { skillDrawerWidth } from './skillHudLayout';
import { uiSizeFactor } from './uiSize';
import { skillDrawerAllowed } from './skillDrawerModel';
import { GrowthModsCache, growthOf, hasUnspentPoints } from './skillTreeModel';
import { NPC_ART_FILTER, STAT_ICON, kaelStandeeUrl, npcCutoutUrl, npcPortraitUrl } from './rpgArt';
import { ShopSeen, type ShopBadgeTab } from './rpgShopSeen';
import { IapShopPanel } from '../iap/panel';
import { OPEN_IAP_TAB_EVENT } from './uiEvents';
import { renderTownShop, townGearRows } from './rpgTownShop';
import { villageHudLayout } from './villageQuest';
import { bagDollLayout, kaelTorsoK } from './bagLayout';
import { viewRect, viewW, viewH } from '../shared/viewport';
import { ENHANCE_TAB_SVG, GEAR_TAB_SVG, GearPanel } from './gearPanel';
import { MASTERY_TAB_SVG } from './masteryPanel';
import { ConsumePanel } from './consumePanel';
import { raidPanelKey, renderRaidPanel } from './raidPanel';
import { SET_TAB_SVG, renderSetPanel } from './setPanel';
import { AVATAR_TAB_SVG, renderAvatarPanel } from './avatarPanel';
import { JobPanel } from './jobPanel';

/** 물약 병(소비 탭·잡화상 물약 탭) · 보석(대장간 보석 탭) · 증류기(연금 탭) 아이콘(D2c) */
const POTION_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 2h6v2h-1v3.3c3 1.2 5 4 5 7.2C19 18.6 15.9 22 12 22s-7-3.4-7-7.5c0-3.2 2-6 5-7.2V4H9zm3 8c-2.5 0-4.6 1.8-4.9 4.2h9.8C16.6 11.8 14.5 10 12 10z"/></svg>';
const GEM_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 3h10l4 6-9 12L3 9zm1.2 2L6 8.3h3.4L10.6 5zm5.2 0 1.2 3.3H18L15.8 5zM6 10.3l5 6.6-2.1-6.6zm6 0h-1.1L12 14l1.1-3.7zm3.1 0L13 16.9l5-6.6z"/></svg>';
const ALCHEMY_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M10 2h4v2h-1v4.5l5.6 9.4c.9 1.6-.2 3.6-2 3.6H7.4c-1.8 0-2.9-2-2-3.6L11 8.5V4h-1zm-1.7 14-1.2 2h9.8l-1.2-2z"/></svg>';

/** 가방 탭. 장비 통합(ECONOMY 14절)으로 「장비」 하나가 6부위 장비를 모두 보인다(옛 「장비함」 탭은 합쳤다) */
type Tab = 'equip' | 'sets' | 'avatar' | 'items' | 'status' | 'mastery' | 'skills';
/** 대장간 창의 오른쪽 탭: 무기(마을 장비) / 강화 / 보석 */
type ForgeTab = 'town' | 'enhance' | 'gem';
/** 잡화상 창의 오른쪽 탭: 방어구·장신구(마을 장비) / 물약·주문서 / 연금 / 젬 */
type ShopTab = 'town' | 'potion' | 'alchemy' | 'iap';
/** 젬 탭 아이콘(인앱 결제) */
const IAP_TAB_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M6 3h12l4 6-10 12L2 9z"/><path d="M2 9h20M9 3l3 18 3-18"/></svg>';
const TAB_LABEL: Record<Tab, string> = { equip: '장비', sets: '세트', avatar: '아바타', items: '소비', status: '상태', mastery: '숙련', skills: '스킬' };
const TAB_ICON: Record<Tab, string> = { equip: GEAR_TAB_SVG, sets: SET_TAB_SVG, avatar: AVATAR_TAB_SVG, items: POTION_TAB_SVG, status: STAT_ICON.maxHp, mastery: MASTERY_TAB_SVG, skills: STAT_ICON.critChance };
/** 「세트」(2026-10-27): 8세트 4부위 그림 칸·발동 효과(setPanel.ts) */
const TABS: readonly Tab[] = ['equip', 'sets', 'avatar', 'items', 'status', 'mastery', 'skills'];
const TONE_COLOR: Record<Tone, string> = { ok: '#7dffa0', warn: '#ffe7a0', err: '#ff7a86' };
const SKILL_ICON = ['dash', 'storm', 'fall'] as const;
const TRAINER_LINE = '주력으로 삼을 기술을 골라라. 쓸수록 손에 익는다.';
const NPC_PLACE: Record<NpcRole, string> = { forge: '대장간', shop: '잡화상', trainer: '검술 교관', jeweler: '보석상', alchemist: '연금술사', raid: '레이드 지휘소' };
/** S7 마을 장비 탭 아이콘(방패 위 금화 — 마을 단계 장비) */
const TOWN_TAB_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3 10 12 3l9 7v2h-2v8h-5v-5h-4v5H5v-8H3zm4 0v8h1v-5h8v5h1v-8l-5-3.9z"/></svg>';

/** 가로 화면 오른쪽 패널(600) 본문의 안쪽 폭(설계 px): 600 − 테두리 4 − 좌우 여백 28 − 스크롤바 8 */
const WIDE_BODY_W = 560;
const STYLE_ID = 'dh-rpg-style';
const PANEL_BG = 'radial-gradient(110% 60% at 50% -8%,rgba(70,92,150,.35),transparent 60%),repeating-linear-gradient(115deg,rgba(255,255,255,.012) 0 2px,transparent 2px 6px),linear-gradient(180deg,rgba(28,38,68,.97) 0%,rgba(18,25,48,.97) 55%,rgba(12,17,34,.97) 100%)';
const FRAME = 'border:2px solid #c8963e;box-shadow:inset 0 0 0 1px rgba(255,215,106,.22),0 0 0 4px rgba(9,12,26,.85),0 0 0 6px rgba(242,193,78,.55),0 18px 50px rgba(0,0,0,.6);';
const CSS =
  '.dh-rpg{--g:#F2C14E;--g2:#ffd76a;--ink:#eef2ff;--mut:#aeb9da}' +
  '.dh-rpg button{font-family:' + FONT + '}' +
  '.dh-rpg button:focus-visible{outline:3px solid #fff;outline-offset:2px;box-shadow:0 0 0 6px rgba(242,193,78,.85)!important}' +
  '.dh-rpg [tabindex="-1"]:focus{outline:none}' +
  '.dh-rpg button:disabled{cursor:not-allowed}' +
  '@keyframes dhRpgIn{from{opacity:0;transform:translateX(18px)}to{opacity:1;transform:none}}' +
  '@keyframes dhRpgArt{from{opacity:0;transform:translateX(-24px)}to{opacity:1;transform:none}}' +
  '@keyframes dhRpgGlow{0%,100%{box-shadow:0 0 0 2px #fff3c0,0 0 14px var(--rc)}50%{box-shadow:0 0 0 2px #fff3c0,0 0 24px var(--rc)}}' +
  '@media (prefers-reduced-motion:reduce){.dh-r-panel,.dh-r-art,.dh-r-tile.sel{animation:none!important}}' +
  // 전체 층: 가로 화면은 배경을 덮지 않고 왼쪽만 살짝 어둡게
  '.dh-r-win{position:relative;box-sizing:border-box;color:var(--ink);font-family:' + FONT + ';overflow:hidden;touch-action:pan-y}' +
  '.dh-r-stage{position:absolute;inset:0;pointer-events:none;overflow:hidden}' +
  '.dh-r-art{position:absolute;bottom:0;pointer-events:none;user-select:none;filter:drop-shadow(0 12px 24px rgba(0,0,0,.55));animation:dhRpgArt .25s ease-out}' +
  // 입상 left는 renderBagSide가 몸통 보정으로 정한다(bagLayout.ts). 높이·가운데는 update가 창 크기마다 CSS 변수로 준다
  '.dh-r-art.kael{left:calc(var(--doll-c,352px) - var(--art-h,600px) * var(--art-k,.361));height:var(--art-h,600px)}' +
  '.dh-r-art.npc{left:10px;height:700px;max-width:640px;object-fit:contain;object-position:left bottom}' +
  '.dh-r-ped{position:absolute;left:var(--doll-c,352px);bottom:20px;width:300px;height:66px;transform:translateX(-50%);border-radius:50%;background:radial-gradient(closest-side,rgba(242,193,78,.42),rgba(242,193,78,.08) 65%,transparent);border:2px solid rgba(242,193,78,.6);box-shadow:0 0 28px rgba(242,193,78,.35),inset 0 0 20px rgba(242,193,78,.35)}' +
  // 위쪽 제목줄
  '.dh-r-top{position:absolute;left:0;top:0;display:flex;align-items:center;gap:14px;height:max(64px,var(--dh-btn-min,48px));padding:0 44px 0 14px;background:linear-gradient(90deg,rgba(10,14,30,.94) 0%,rgba(16,22,44,.9) 78%,rgba(16,22,44,0) 100%);border-bottom:1.5px solid rgba(242,193,78,.55);clip-path:polygon(0 0,100% 0,calc(100% - 26px) 100%,0 100%);z-index:3}' +
  '.dh-r-back{display:grid;place-items:center;width:var(--dh-btn-min,48px);height:var(--dh-btn-min,48px);border:0;background:transparent;cursor:pointer;color:var(--g);padding:0}' +
  '.dh-r-back svg{width:40px;height:40px;filter:drop-shadow(0 0 6px rgba(242,193,78,.5))}' +
  '.dh-r-title{font:900 30px ' + FONT + ';letter-spacing:1px;color:#fff;text-shadow:0 2px 6px rgba(0,0,0,.8);white-space:nowrap}' +
  '.dh-r-esc{font:700 12px ' + FONT + ';color:var(--mut);border:1px solid rgba(174,185,218,.5);border-radius:4px;padding:0 5px}' +
  '.dh-r-gold{position:absolute;right:22px;top:12px;display:flex;align-items:center;gap:10px;min-width:220px;justify-content:space-between;padding:5px 18px 5px 6px;border-radius:999px;background:linear-gradient(180deg,rgba(8,12,28,.92),rgba(22,30,58,.92));border:1.5px solid rgba(242,193,78,.65);font:900 22px ' + FONT_NUM + ';color:#fff;white-space:nowrap;z-index:3}' +
  '.dh-r-coin{display:inline-block;flex:0 0 auto;width:28px;height:28px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff6c0,#f2c14e 45%,#a8641a 100%);box-shadow:inset 0 0 0 2px rgba(110,64,8,.65),0 0 7px rgba(242,193,78,.65)}' +
  '.dh-r-coin.s{width:22px;height:22px}' +
  // 오른쪽 패널
  '.dh-r-panel{position:absolute;right:20px;top:max(76px,calc(var(--dh-btn-min,48px) + 12px));bottom:18px;width:600px;display:flex;flex-direction:column;border-radius:12px;background:' + PANEL_BG + ';' + FRAME + 'animation:dhRpgIn .22s ease-out;z-index:2}' +
  '.dh-r-panel::before,.dh-r-panel::after{content:"";position:absolute;width:22px;height:22px;border:3px solid var(--g);pointer-events:none}' +
  '.dh-r-panel::before{left:6px;top:6px;border-right:0;border-bottom:0;border-radius:8px 0 0 0}' +
  '.dh-r-panel::after{right:6px;bottom:6px;border-left:0;border-top:0;border-radius:0 0 8px 0}' +
  '.dh-r-notice{display:none;margin:12px 14px 0;padding:10px 14px;border-radius:8px;background:linear-gradient(90deg,rgba(255,42,61,.25),rgba(255,42,61,.08));border:1.5px solid rgba(255,42,61,.55);font:600 14px ' + FONT + ';line-height:1.45;flex:0 0 auto}' +
  '.dh-r-ph{display:none;flex-direction:column;align-items:center;gap:4px;padding:16px 16px 0;flex:0 0 auto}' +
  '.dh-r-ph b{font:900 30px ' + FONT + ';' + GOLD_TEXT + 'filter:drop-shadow(0 2px 0 rgba(0,0,0,.7))}' +
  '.dh-r-ph span{font:600 15px ' + FONT + ';color:#c9d3f0}' +
  '.dh-r-tabs{display:none;gap:8px;padding:14px 14px 0;flex:0 0 auto}' +
  '.dh-r-tab{flex:1 1 0;display:flex;align-items:center;justify-content:center;gap:8px;min-height:var(--dh-btn-min,52px);border-radius:10px;border:1.5px solid rgba(242,193,78,.38);background:linear-gradient(180deg,#24305a,#151d38);color:#ccd5f2;font:800 19px ' + FONT + ';cursor:pointer}' +
  '.dh-r-tab svg{width:24px;height:24px;color:#c9d3f0;flex:0 0 auto}' +
  '.dh-r-tabs.t7{gap:5px;padding-left:10px;padding-right:10px}.dh-r-tabs.t7 .dh-r-tab{min-width:0;gap:4px;padding:0 2px;font-size:16px}.dh-r-tabs.t7 .dh-r-tab svg{width:20px;height:20px}' +
  // 탭이 6개(아바타 추가)라 좁은 가로 화면에서 글자가 두 줄로 꺾이지 않게 한다
  '.dh-r-tab span{white-space:nowrap;letter-spacing:-.02em}' +
  '.dh-r-tab[aria-selected="true"]{background:linear-gradient(180deg,#ffe597,#e1a536);color:#2a1d05;border-color:#fff1b8;box-shadow:0 0 16px rgba(242,193,78,.5),inset 0 1px 0 rgba(255,255,255,.6)}' +
  '.dh-r-tab[aria-selected="true"] svg{color:#4a2e05}' +
  '.dh-r-body{flex:1 1 auto;min-height:0;overflow-y:auto;padding:12px 14px 12px;display:flex;flex-direction:column;gap:10px;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:rgba(242,193,78,.5) transparent}' +
  '.dh-r-msg{flex:0 0 auto;min-height:0;padding:0 16px;font:800 15px ' + FONT + '}' +
  '.dh-r-msg:not(:empty){padding:8px 16px 10px;border-top:1.5px solid rgba(242,193,78,.25)}' +
  '.dh-r-h{display:flex;align-items:center;gap:10px;font:900 14px ' + FONT + ';color:#7fd3e6;letter-spacing:1.5px}' +
  '.dh-r-h::after{content:"";flex:1 1 auto;height:1px;background:linear-gradient(90deg,rgba(127,211,230,.5),transparent)}' +
  // 왼쪽 가방 겹층: 세로 메뉴, 이름, 장착 칸, 능력치 상자
  '.dh-r-who{position:absolute;left:18px;top:max(80px,calc(var(--dh-btn-min,48px) + 12px));z-index:2;text-shadow:0 2px 6px rgba(0,0,0,.9)}' +
  '.dh-r-who b{display:block;font:900 32px ' + FONT + ';color:#fff}' +
  '.dh-r-who span{display:block;font:700 15px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-who em{font-style:normal;font:900 18px ' + FONT_NUM + ';color:var(--g2)}' +
  '.dh-r-slots{position:absolute;left:150px;top:178px;display:flex;flex-direction:column;gap:12px;z-index:2}' +
  '.dh-r-slot{display:flex;flex-direction:column;gap:4px;align-items:flex-start}' +
  '.dh-r-slot>span{display:flex;align-items:center;gap:4px;font:800 13px ' + FONT + ';color:var(--g2);padding:1px 8px;border-radius:6px;background:rgba(9,12,26,.8);border:1px solid rgba(242,193,78,.45)}' +
  '.dh-r-slot>span svg{width:14px;height:14px}' +
  // 장착 6부위(장비 통합, ECONOMY 14절): 카엘 입상 왼쪽 무기·투구·갑옷, 오른쪽 장갑·신발·목걸이
  '.dh-r-gdoll{position:absolute;left:var(--doll-l,142px);top:var(--doll-t,150px);width:var(--doll-w,420px);box-sizing:border-box;display:grid;grid-template-columns:78px 1fr 78px;grid-template-rows:repeat(3,auto);row-gap:10px;z-index:2;pointer-events:none}' +
  '.dh-r-gdoll .dh-r-gslot{pointer-events:auto}' +
  '.dh-r-gdoll .dh-r-gslot:nth-child(1){grid-area:1/1}.dh-r-gdoll .dh-r-gslot:nth-child(2){grid-area:2/1}.dh-r-gdoll .dh-r-gslot:nth-child(3){grid-area:3/1}' +
  '.dh-r-gdoll .dh-r-gslot:nth-child(4){grid-area:1/3}.dh-r-gdoll .dh-r-gslot:nth-child(5){grid-area:2/3}.dh-r-gdoll .dh-r-gslot:nth-child(6){grid-area:3/3}' +
  '.dh-r-gslot{display:flex;flex-direction:column;gap:3px;align-items:center;min-width:0}' +
  '.dh-r-gslot .lab{font:800 12px ' + FONT + ';color:var(--g2);padding:0 7px;border-radius:6px;background:rgba(9,12,26,.8);border:1px solid rgba(242,193,78,.45);white-space:nowrap}' +
  '.dh-r-gdoll .dh-g-tile{width:74px;height:74px}' +
  // 터치 기기: 넓은 창 인형 칸도 창 배율(폰 가로 약 0.54)에 줄어 40px로 보였다 → 겉 크기 44px 이상(RG-008 재측정)
  '@media (pointer:coarse){.dh-r-gdoll .dh-g-tile{width:max(74px,var(--dh-hit-min,44px));height:max(74px,var(--dh-hit-min,44px))}}' +
  '.dh-r-gmini{display:none;grid-template-columns:repeat(6,minmax(0,1fr));gap:6px}' +
  '.dh-r-win.narrow .dh-r-gmini{display:grid}' +
  '.dh-r-gmini .dh-g-tile{width:100%}' +
  // 터치 기기: 좁은 창 장착 칸(rpg-slot-*)의 겉 크기가 창 배율에 줄어 40px로 보이던 것을 최소 44px로(출시 게이트 RG-008)
  '@media (pointer:coarse){.dh-r-gmini{grid-template-columns:repeat(6,minmax(var(--dh-hit-min,44px),1fr))}.dh-r-gmini .dh-g-tile{min-width:var(--dh-hit-min,44px);min-height:var(--dh-hit-min,44px)}}' +
  '.dh-r-gmini .lab{font-size:11px;padding:0 4px}' +
  '.dh-r-sbox{position:absolute;left:12px;bottom:16px;width:216px;padding:10px 12px;border-radius:10px;background:rgba(10,14,30,.88);border:1.5px solid rgba(242,193,78,.5);z-index:2}' +
  '.dh-r-sr{display:flex;align-items:center;gap:8px;padding:4px 0;font:700 14px ' + FONT + ';color:#dfe6ff;border-bottom:1px solid rgba(242,193,78,.14)}' +
  '.dh-r-sr svg{width:16px;height:16px;color:var(--g)}' +
  '.dh-r-sr b{margin-left:auto;font:900 15px ' + FONT_NUM + ';color:#fff}' +
  '.dh-r-sbox .dh-r-btn{width:100%;margin-top:8px;min-height:var(--dh-btn-min,44px);font-size:16px}' +
  // 왼쪽 NPC 대사 상자
  '.dh-r-say{position:absolute;left:40px;bottom:36px;width:470px;padding:22px 22px 18px;border-radius:12px;background:linear-gradient(180deg,rgba(18,25,48,.95),rgba(10,14,30,.95));' + FRAME + 'z-index:2;font:600 19px ' + FONT + ';line-height:1.6;color:#fff}' +
  '.dh-r-say i{position:absolute;left:18px;top:-17px;font-style:normal;padding:3px 18px;border-radius:6px;background:linear-gradient(180deg,#26335a,#121a33);border:1.5px solid var(--g);font:900 17px ' + FONT + ';color:var(--g2)}' +
  '.dh-r-say::after{content:"▼";position:absolute;right:16px;bottom:8px;font-size:14px;color:var(--g)}' +
  // 타일·등급 틀
  '.dh-r-tile{position:relative;box-sizing:border-box;width:88px;height:88px;flex:0 0 auto;padding:0;border-radius:10px;cursor:pointer;border:2px solid var(--rc,#6b7488);' +
  'background:radial-gradient(circle at 50% 32%,var(--rbg1,#333a4a),var(--rbg2,#141826) 75%);box-shadow:inset 0 0 0 1px rgba(255,255,255,.1),inset 0 -14px 20px rgba(0,0,0,.4),0 3px 0 rgba(0,0,0,.5)}' +
  '.dh-r-tile img{position:absolute;inset:7%;width:86%;height:86%;object-fit:contain;filter:drop-shadow(0 3px 3px rgba(0,0,0,.65));pointer-events:none}' +
  '.dh-r-tile.sel{animation:dhRpgGlow 1.6s ease-in-out infinite;box-shadow:0 0 0 2px #fff3c0,0 0 18px var(--rc)}' +
  '.dh-r-tile .tag{position:absolute;left:-2px;top:-2px;padding:1px 7px;border-radius:9px 0 8px 0;background:var(--g);color:#2a1d05;font:900 11px ' + FONT + '}' +
  '.dh-r-tile .cnt{position:absolute;right:5px;bottom:2px;font:900 13px ' + FONT_NUM + ';color:#fff;text-shadow:0 1px 2px #000}' +
  '.dh-r-tile.empty{cursor:default;border:1.5px solid rgba(174,185,218,.16);background:radial-gradient(circle,rgba(30,40,70,.55),rgba(8,11,24,.65));box-shadow:inset 0 0 12px rgba(0,0,0,.5)}' +
  '.dh-r-tile.empty::after{content:"";position:absolute;left:50%;top:50%;width:14px;height:14px;transform:translate(-50%,-50%) rotate(45deg);border:1.5px solid rgba(174,185,218,.2)}' +
  '.dh-r-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px}' +
  '.dh-r-grid .dh-r-tile{width:100%;height:auto;aspect-ratio:1}' +
  '.dh-r-cap{display:block;margin-top:4px;font:700 12px ' + FONT + ';color:var(--mut);text-align:center;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}' +
  '.dh-r-cap.on{color:var(--rc)}' +
  // 카드 공통
  '.dh-r-card{position:relative;box-sizing:border-box;border-radius:10px;border:1.5px solid rgba(242,193,78,.4);background:linear-gradient(180deg,rgba(32,43,78,.95),rgba(12,17,34,.95));padding:12px 14px;box-shadow:inset 0 1px 0 rgba(255,255,255,.07),0 4px 14px rgba(0,0,0,.3)}' +
  '.dh-r-card.on{border-color:var(--g);box-shadow:inset 0 0 0 1px var(--g),0 0 22px rgba(242,193,78,.3)}' +
  '.dh-r-chip{display:inline-block;padding:1px 9px;border-radius:5px;font:900 12px ' + FONT + ';white-space:nowrap;vertical-align:middle}' +
  '.dh-r-chip.rar{border:1.5px solid var(--rc);color:var(--rc);background:rgba(0,0,0,.25)}' +
  '.dh-r-chip.eq{background:var(--g);color:#2a1d05;margin-left:8px}.dh-r-chip.own{border:1.5px solid #7dffa0;color:#7dffa0;margin-left:8px}.dh-r-chip.role{border:1.5px solid #7fd3e6;color:#7fd3e6;margin-left:8px}' +
  '.dh-r-btn{min-height:var(--dh-btn-min,48px);min-width:96px;padding:8px 22px;border-radius:9px;font:900 18px ' + FONT + ';letter-spacing:.5px;white-space:nowrap;cursor:pointer;border:2px solid var(--g);box-shadow:0 3px 0 rgba(0,0,0,.5);' +
  'background:linear-gradient(180deg,#2a3864,#18203d);color:#e9edfb}' +
  '.dh-r-btn.pri{background:linear-gradient(180deg,#ffe597 0%,#f2c14e 45%,#d4922a 100%);color:#2a1d05;border-color:#fff0b0;box-shadow:inset 0 1px 0 rgba(255,255,255,.7),0 3px 0 #7a4f12,0 0 14px rgba(242,193,78,.35)}' +
  '.dh-r-btn.poor{background:linear-gradient(180deg,rgba(90,16,28,.75),rgba(50,8,16,.75));border-color:#ff5466;color:#ff8a96;font-size:16px;white-space:normal;line-height:1.25}' +
  '.dh-r-btn.held{background:rgba(10,14,30,.6);border-color:rgba(242,193,78,.55);color:#e9edfb;box-shadow:none}' +
  '.dh-r-btn:disabled:not(.held){opacity:.45;box-shadow:none}' +
  '.dh-r-btn:not(:disabled):active{transform:translateY(2px);box-shadow:0 1px 0 rgba(0,0,0,.5)}' +
  // 가방 상세 카드
  '.dh-r-detail{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:6px 14px;align-items:start}' +
  '.dh-r-detail>.dh-r-tile{width:92px;height:92px;cursor:default}' +
  '.dh-r-iname{font:900 22px ' + FONT + ';color:var(--rc)}' +
  '.dh-r-kind{font:700 13px ' + FONT + ';color:var(--mut);margin-top:2px}' +
  '.dh-r-big{display:flex;align-items:center;gap:10px;margin-top:6px;font:800 15px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-big svg{width:20px;height:20px;color:var(--g)}' +
  '.dh-r-big b{font:900 30px ' + FONT_NUM + ';' + GOLD_TEXT + '}' +
  '.dh-r-big.s b{font-size:20px}' +
  '.dh-r-cmp{min-width:150px;padding-left:12px;border-left:1px solid rgba(242,193,78,.25);font:700 13px ' + FONT + ';color:var(--mut)}' +
  '.dh-r-cmp div{display:flex;justify-content:space-between;gap:8px;margin-top:6px;color:#dfe6ff}' +
  '.dh-r-up{color:#7dffa0;font-weight:900}.dh-r-dn{color:#ff7a86;font-weight:900}' +
  '.dh-r-desc{grid-column:1/-1;font:500 14px ' + FONT + ';color:#c9d3f0;line-height:1.5;border-top:1px solid rgba(242,193,78,.18);padding-top:8px}' +
  '.dh-r-act{grid-column:1/-1;display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
  '.dh-r-act .dh-r-btn{width:100%}' +
  // 대장간 행
  '.dh-r-row{display:grid;grid-template-columns:auto minmax(0,1fr) 150px;gap:12px;align-items:center;padding:10px 12px}' +
  '.dh-r-row .dh-r-tile{width:84px;height:84px;cursor:default}' +
  '.dh-r-rn{font:900 20px ' + FONT + ';color:#fff;margin-top:4px;padding-bottom:4px;border-bottom:1px solid rgba(242,193,78,.22)}' +
  '.dh-r-rs{display:flex;flex-wrap:wrap;align-items:center;gap:2px 14px;margin-top:4px;font:700 15px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-rs b{font:900 16px ' + FONT_NUM + ';color:#fff;margin-left:6px}' +
  '.dh-r-buy{display:flex;flex-direction:column;align-items:stretch;gap:6px}' +
  '.dh-r-buy .dh-r-btn{width:100%}' +
  '.dh-r-price{display:flex;align-items:center;gap:8px;font:900 22px ' + FONT_NUM + ';color:#fff;white-space:nowrap;padding-left:6px}' +
  '.dh-r-short{font:700 12px ' + FONT + ';color:#ff7a86;text-align:center}' +
  '.dh-r-short.base{color:var(--mut)}' +
  // 잡화상 큰 카드
  '.dh-r-cards{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}' +
  '.dh-r-big-card{display:flex;flex-direction:column;gap:8px;border-color:var(--rc);padding:10px}' +
  '.dh-r-big-card h4{margin:0;display:flex;justify-content:space-between;align-items:center;font:900 21px ' + FONT + ';color:#fff}' +
  '.dh-r-pic{position:relative;height:170px;border-radius:8px;border:2px solid var(--rc);background:radial-gradient(circle at 50% 40%,var(--rbg1),var(--rbg2) 78%);box-shadow:inset 0 0 26px rgba(0,0,0,.45),0 0 16px color-mix(in srgb,var(--rc) 35%,transparent)}' +
  '.dh-r-pic img{position:absolute;inset:6%;width:88%;height:88%;object-fit:contain;filter:drop-shadow(0 6px 8px rgba(0,0,0,.6))}' +
  '.dh-r-big-card p{margin:0;font:500 14px ' + FONT + ';color:#c9d3f0;line-height:1.45;min-height:40px}' +
  '.dh-r-hp{display:flex;align-items:center;gap:8px;padding:6px 0;border-top:1px solid rgba(242,193,78,.22);border-bottom:1px solid rgba(242,193,78,.22);font:700 15px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-hp svg{width:18px;height:18px;color:#dfe6ff}' +
  '.dh-r-hp b{font:900 18px ' + FONT_NUM + ';color:#fff;margin-left:auto}' +
  '.dh-r-cprice{display:flex;justify-content:center;align-items:center;gap:8px;font:900 26px ' + FONT_NUM + ';color:#fff}' +
  '.dh-r-big-card .dh-r-btn{width:100%;font-size:22px}' +
  '.dh-r-big-card .dh-r-btn.poor{font-size:17px}' +
  // 상태
  '.dh-r-stat{display:grid;grid-template-columns:52px 150px minmax(0,1fr);align-items:center;gap:12px}' +
  '.dh-r-sico{display:grid;place-items:center;width:52px;height:52px;color:var(--g)}' +
  '.dh-r-sico svg{width:40px;height:40px;filter:drop-shadow(0 0 6px rgba(242,193,78,.45))}' +
  '.dh-r-sl{font:800 15px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-st{font:900 32px ' + FONT_NUM + ';' + GOLD_TEXT + 'line-height:1.05}' +
  '.dh-r-bar{display:flex;height:16px;border-radius:4px;overflow:hidden;background:rgba(0,0,0,.45);box-shadow:inset 0 0 0 1px rgba(255,255,255,.08);gap:2px}' +
  '.dh-r-bar i{display:grid;place-items:center;height:100%;font:800 10px ' + FONT + ';font-style:normal;color:rgba(255,255,255,.85);overflow:hidden;white-space:nowrap}' +
  '.dh-r-leg{display:flex;justify-content:space-between;gap:8px;margin-top:5px;font:700 13px ' + FONT + ';color:var(--mut)}' +
  '.dh-r-leg b{font:900 14px ' + FONT_NUM + ';margin-left:5px;color:#dfe6ff}' +
  '.dh-r-focus{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:6px 14px;align-items:center}' +
  '.dh-r-focus>img{grid-row:span 2;width:72px;height:72px;border-radius:50%;object-fit:cover;border:3px solid var(--g);box-shadow:0 0 14px rgba(255,42,61,.45)}' +
  '.dh-r-focus h5{margin:0;font:900 20px ' + FONT + ';color:#fff}' +
  '.dh-r-pill{display:flex;align-items:center;gap:6px;padding:4px 12px;border-radius:999px;border:1.5px solid rgba(127,211,230,.6);font:800 14px ' + FONT + ';color:#dfe6ff;white-space:nowrap}' +
  '.dh-r-pill svg{width:16px;height:16px;color:#7fd3e6}.dh-r-pill b{font:900 16px ' + FONT_NUM + ';color:#9fe4ff}' +
  '.dh-r-focus p{grid-column:2/-1;margin:0;font:500 14px ' + FONT + ';color:#c9d3f0}' +
  '.dh-r-note{font:700 14px ' + FONT + ';color:var(--mut)}' +
  // 대장간 개선 탭: 현재 카드 › 개선 후 카드, 능력치 비교표, 안내 줄, 금화와 개선 버튼(설계도 노드 ui-npc-forge-upgrade)
  '.dh-r-ug{display:flex;flex-direction:column;gap:10px;flex:1 1 auto;min-height:0;padding:10px;border-radius:10px;border:1.5px solid rgba(242,193,78,.5);background:linear-gradient(180deg,rgba(14,20,42,.7),rgba(8,12,28,.7));box-shadow:inset 0 0 0 1px rgba(242,193,78,.12)}' +
  '.dh-r-ug-pair{display:grid;grid-template-columns:minmax(0,1fr) 44px minmax(0,1fr);align-items:stretch;gap:6px;flex:1 1 auto;max-height:290px}' +
  '.dh-r-ug-arrow{align-self:center}' +
  '.dh-r-ug-card{position:relative;display:flex;flex-direction:column;align-items:center;gap:6px;padding:0 8px 10px;border-radius:8px;border:1.5px solid var(--rc);background:linear-gradient(180deg,rgba(30,42,78,.85),rgba(12,17,34,.9));overflow:hidden;transition:opacity .15s}' +
  '.dh-r-ug-card.dim{opacity:.45;filter:saturate(.4)}' +
  '.dh-r-ug-card.nxt{border-color:var(--g);box-shadow:0 0 18px rgba(242,193,78,.28),inset 0 0 0 1px rgba(255,215,106,.25)}' +
  '.dh-r-ug-h{align-self:stretch;margin:0 -8px 2px;padding:3px 0 4px;text-align:center;font:900 17px ' + FONT + ';color:#7cc4ff;background:linear-gradient(180deg,rgba(40,90,170,.55),rgba(24,48,98,.55));border-bottom:1.5px solid rgba(92,178,255,.5)}' +
  '.dh-r-ug-card.nxt .dh-r-ug-h{color:var(--g2);background:linear-gradient(180deg,rgba(150,98,24,.6),rgba(86,54,12,.6));border-bottom-color:rgba(242,193,78,.6)}' +
  '.dh-r-ug-card .dh-r-tile{width:104px;height:104px;cursor:default;overflow:visible;margin-top:auto;flex:0 0 auto}' +
  '.dh-r-ug-card .dh-r-tile .dh-r-chip{position:absolute;right:-2px;top:-2px;background:rgba(40,16,6,.9);z-index:1}' +
  '.dh-r-ug-n{margin-bottom:auto;font:900 21px ' + FONT + ';color:#fff;text-align:center;line-height:1.15}' +
  '.dh-r-ug-arrow{display:grid;place-items:center;color:var(--g2);filter:drop-shadow(0 0 8px rgba(242,193,78,.75))}' +
  '.dh-r-ug-arrow svg{width:38px;height:56px}' +
  '.dh-r-ug-tab{border-radius:8px;border:1.5px solid rgba(242,193,78,.4);background:rgba(8,12,28,.6)}' +
  '.dh-r-ug-row{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr) 22px minmax(0,1fr) minmax(0,1fr);align-items:center;gap:6px;padding:0 14px;min-height:44px;font:800 18px ' + FONT + ';color:#dfe6ff}' +
  '.dh-r-ug-row+.dh-r-ug-row{border-top:1px solid rgba(242,193,78,.2)}' +
  '.dh-r-ug-row b{font:900 20px ' + FONT_NUM + ';color:#fff;text-align:center}' +
  '.dh-r-ug-row b.to{color:#7dffa0}.dh-r-ug-row b.to.dn{color:#ff7a86}' +
  '.dh-r-ug-row .chev{color:var(--g);text-align:center;font:900 20px ' + FONT + '}' +
  '.dh-r-ug-row .df{font:900 15px ' + FONT_NUM + ';color:#7dffa0;text-align:right;white-space:nowrap}.dh-r-ug-row .df.dn{color:#ff7a86}' +
  '.dh-r-ug-hint{display:flex;align-items:center;justify-content:center;gap:10px;min-height:36px;padding:4px 12px;border-radius:8px;border:1px solid rgba(242,193,78,.25);background:rgba(8,12,28,.55);font:700 15px ' + FONT + ';color:#dfe6ff;text-align:center}' +
  '.dh-r-ug-hint .ex{display:grid;place-items:center;flex:0 0 auto;width:22px;height:22px;border-radius:50%;background:#dfe6ff;color:#121a33;font:900 14px ' + FONT + '}' +
  '.dh-r-ug-hint.warn{color:#ffe7a0;border-color:rgba(255,231,160,.5)}.dh-r-ug-hint.warn .ex{background:#ffe7a0}' +
  '.dh-r-ug-hint.ok{color:#7dffa0;border-color:rgba(125,255,160,.45)}.dh-r-ug-hint.ok .ex{background:#7dffa0}' +
  '.dh-r-ug-price{display:flex;align-items:center;justify-content:center;gap:10px;font:900 26px ' + FONT_NUM + ';color:#fff}' +
  '.dh-r-ug-go{width:100%;min-height:max(var(--dh-btn-min,48px),54px);font-size:24px;letter-spacing:2px}' +
  '.dh-r-ug-go.poor{font-size:20px;letter-spacing:.5px}' +
  // 낮은 가로 화면(폰 가로, 배율 0.75 미만): 터치 최소 높이가 설계 좌표로 커지므로 교관 창이 한 화면에 들어오게 카드 여백을 줄인다
  '.dh-r-win.tight .dh-r-ph{padding-top:8px}.dh-r-win.tight .dh-r-ph b{font-size:24px}' +
  '.dh-r-win.tight .dh-r-body{gap:6px;padding-top:8px}' +
  // 폰 가로 개선 탭: 잠김 상태 안내가 길어 버튼이 카드 밖으로 1.4px 넘쳤다(2026-10-19 로컬 러너 844x390). 간격만 줄이고 카드 쌍이 줄어들 수 있게 한다
  '.dh-r-win.tight .dh-r-ug{gap:6px;padding:8px}.dh-r-win.tight .dh-r-ug-pair{min-height:0}.dh-r-win.tight .dh-r-ug-hint{min-height:30px}' +
  // 세로 화면(폰): 틀 안에 한 칸으로 쌓는다
  '.dh-r-win.narrow{display:flex;flex-direction:column;border-radius:14px;background:' + PANEL_BG + ';' + FRAME + '}' +
  '.dh-r-win.narrow .dh-r-stage,.dh-r-win.narrow .dh-r-who,.dh-r-win.narrow .dh-r-slots,.dh-r-win.narrow .dh-r-sbox,.dh-r-win.narrow .dh-r-say{display:none}' +
  '.dh-r-win.narrow .dh-r-top{position:relative;flex:0 0 auto;clip-path:none;padding:0 12px 0 4px;background:rgba(10,14,30,.6)}' +
  '.dh-r-win.narrow .dh-r-title{font-size:24px}' +
  '.dh-r-win.narrow .dh-r-gold{top:12px;right:10px;min-width:0;font-size:18px}' +
  '.dh-r-win.narrow .dh-r-panel{position:relative;inset:auto;width:auto;flex:1 1 auto;min-height:0;border:0;box-shadow:none;background:none;border-radius:0;animation:none}' +
  '.dh-r-win.narrow .dh-r-panel::before,.dh-r-win.narrow .dh-r-panel::after{display:none}' +
  '.dh-r-win.narrow .dh-r-grid{grid-template-columns:repeat(4,minmax(0,1fr))}' +
  '.dh-r-win.narrow .dh-r-stat{grid-template-columns:44px minmax(0,1fr)}.dh-r-win.narrow .dh-r-stat>div:last-child{grid-column:1/-1}' +
  '.dh-r-win.narrow .dh-r-cards{grid-template-columns:1fr}' +
  '.dh-r-win.narrow .dh-r-row{grid-template-columns:auto minmax(0,1fr)}.dh-r-win.narrow .dh-r-buy{grid-column:1/-1}' +
  '.dh-r-win.narrow .dh-r-detail{grid-template-columns:auto minmax(0,1fr)}.dh-r-win.narrow .dh-r-cmp{grid-column:1/-1;border-left:0;padding-left:0}' +
  '.dh-r-mini{display:none;align-items:center;gap:12px;padding:10px 12px;border-radius:10px;background:rgba(10,14,30,.55);border:1px solid rgba(242,193,78,.3)}' +
  '.dh-r-win.narrow .dh-r-mini{display:flex}' +
  '.dh-r-mini .face{width:72px;height:72px;flex:0 0 auto;border-radius:50%;object-fit:cover;border:3px solid var(--g);box-shadow:0 0 0 3px rgba(9,12,26,.92)}' +
  '.dh-r-mini .txt{flex:1 1 auto;min-width:0}' +
  '.dh-r-mini .txt b{display:block;font:900 19px ' + FONT + '}' +
  '.dh-r-mini .txt span{font:600 14px ' + FONT + ';color:var(--mut);line-height:1.4}' +
  '.dh-r-mini .dh-r-tile{width:64px;height:64px}' +
  // 마을 HUD
  '.dh-r-hudgold{display:flex;align-items:center;gap:8px;padding:5px 16px 5px 6px;border:2px solid var(--g);border-radius:999px;background:linear-gradient(180deg,rgba(26,34,62,.95),rgba(12,17,34,.95));font:900 19px ' + FONT_NUM + ';color:var(--g2);letter-spacing:.5px;white-space:nowrap;box-shadow:0 3px 10px rgba(0,0,0,.45)}' +
  '.dh-r-bag{display:flex;align-items:center;gap:8px;pointer-events:auto;cursor:pointer;min-height:var(--dh-btn-min,44px);padding:6px 16px 6px 8px;border-radius:12px;border:2px solid var(--g);background:linear-gradient(180deg,#2a3864,#151d38);color:var(--g);font:900 17px ' + FONT + ';white-space:nowrap;box-shadow:0 3px 0 rgba(0,0,0,.45)}' +
  '.dh-r-bag svg{width:26px;height:26px}' +
  // 안 쓴 스킬 포인트가 있으면 가방 버튼·교관 안내 모서리에 금색 점
  '.dh-r-bag{position:relative}' +
  // HUD 정리: 둥근 아이콘 단추(글자는 숨기고 aria-label·툴팁으로)
  '.dh-r-ico{width:var(--dh-btn-min,44px);height:var(--dh-btn-min,44px);min-height:0;padding:0;justify-content:center;border-radius:50%;border:1px solid rgba(242,193,78,.65);background:rgba(10,16,32,.62);color:#f4ecd8;box-shadow:none}' +
  '.dh-r-ico svg{width:22px;height:22px}' +
  '.dh-r-ico>span:not(.dh-r-dot){display:none}' +
  '.dh-r-ico .dh-r-dot{right:-2px;top:-2px;width:11px;height:11px}' +
  '.dh-r-icorow .dh-hp-panel{left:auto;right:0}' +
  '.dh-r-dot{position:absolute;right:-5px;top:-5px;width:15px;height:15px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff6c0,#f2c14e 55%,#a8641a);box-shadow:0 0 0 2px rgba(9,12,26,.92),0 0 10px rgba(242,193,78,.95);pointer-events:none}' +
  '.dh-r-dot[hidden]{display:none}' +
  // 스킬 트리(가방 스킬 탭·교관 창): 가로 화면에서는 패널을 넓히고 왼쪽 입상·능력치 상자는 숨긴다
  '.dh-r-win.wide .dh-r-panel{left:150px;width:auto}' +
  '.dh-r-win.wide.wide-trainer .dh-r-panel{left:20px}' +
  '.dh-r-win.wide .dh-r-stage,.dh-r-win.wide .dh-r-who,.dh-r-win.wide .dh-r-slots,.dh-r-win.wide .dh-r-sbox,.dh-r-win.wide .dh-r-say{display:none}';

const QUEST_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M7 3h11a2 2 0 0 1 2 2v12a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-1h3V5a2 2 0 0 1 0-2zm3 5v2h7V8zm0 4v2h7v-2zm-6 5v1a2 2 0 0 0 2 2h8.2A4 4 0 0 1 14 17z"/></svg>';
const BAG_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M9 3h6a2 2 0 0 1 2 2v1h1.5A2.5 2.5 0 0 1 21 8.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5A2.5 2.5 0 0 1 5.5 6H7V5a2 2 0 0 1 2-2zm0 2v1h6V5zm-4 5v3h14v-3zm5 4v2h4v-2z"/></svg>';
const BACK_SVG = '<svg viewBox="0 0 40 40" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="3" d="M20 3 37 20 20 37 3 20z"/><path fill="currentColor" d="M23 11 14 20l9 9 2.4-2.4L18.8 20l6.6-6.6z"/></svg>';
const ARMOR_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8 3h8l1 2 4 1-1 6-3-1v9H7v-9l-3 1-1-6 4-1zm2 2-1 2h6l-1-2z"/></svg>';

/** 배율 s(scale/zoom)이 적용된 뒤에도 화면 높이 44px이 되도록 하는 CSS 최소 높이(px) */
function minCssPx(s: number): number { return Math.ceil((44 / Math.min(1, Math.max(0.1, s))) * 10) / 10; }

/** ev: 소리를 게임 이벤트(Ev.Trade·Ev.Menu)가 정하는 버튼(공용 클릭음을 끈다) */
interface BtnOpt { primary?: boolean; disabled?: boolean; poor?: boolean; held?: boolean; testid?: string; focusKey?: string; ev?: boolean }

/** 그림 <img>. 받기에 실패하면 숨긴다(틀·글자는 그대로 남는다) */
function art(src: string, cls: string, parent: HTMLElement): HTMLImageElement {
  const im = el('img', '', parent);
  im.className = cls;
  im.alt = '';
  im.draggable = false;
  im.decoding = 'async';
  im.addEventListener('error', () => { im.style.visibility = 'hidden'; }, { once: true });
  im.src = src;
  return im;
}

export class RpgUi {
  private readonly root: HTMLDivElement;
  // 마을 HUD
  private readonly hudRow: HTMLDivElement;
  private kHudOn: boolean | null = null;
  private rowDirty = true;
  private readonly goldChip: HTMLDivElement;
  private readonly goldVal: HTMLSpanElement;
  private readonly invBtn: HTMLButtonElement;
  private readonly prompt: HTMLButtonElement;
  // 창
  private readonly backdrop: HTMLDivElement;
  /** 창이 열리거나 닫힐 때만 알린다(커뮤니티 단추 접기). 이벤트 하나, 매 프레임 아님 */
  private windowChanged(): void { if (typeof window !== 'undefined') window.dispatchEvent(new Event(RPG_WINDOW_EVENT)); }
  private readonly dialog: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly side: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly panelGold: HTMLDivElement;
  private readonly notice: HTMLDivElement;
  private readonly ph: HTMLDivElement;
  private readonly tabs: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private readonly msg: HTMLDivElement;
  private readonly invDot: HTMLSpanElement;
  private readonly promptLabel: HTMLSpanElement;
  private readonly promptDot: HTMLSpanElement;
  /** 스킬 트리 화면(가방 스킬 탭·교관 창이 같은 부품을 쓴다) */
  private readonly tree = new SkillTreeUi();
  private readonly drawer = new SkillDrawerUi(this.tree);
  private readonly drawerScroll = new Map<string, number>();
  private kSlotsRev = -1;
  private kHero = '';
  /** 미리보기(장비 비교·개선·주력)에 넘기는 성장 보정(레벨·랭크가 바뀔 때만 다시 계산) */
  private readonly modsCache = new GrowthModsCache();
  private get mods() { return this.modsCache.get(growthOf(this.view)); }
  /** 장착 드롭 장비 합계(미리보기가 실제 능력치와 같게, c009 리뷰 P2) */
  private get gearTotals() { return gearTotalsOf(this.view.gear, this.view.heroId); }
  /** 스킬 배우기·초기화 결과(GameView.growth.last.seq)를 한 번만 알리는 문 */
  private readonly growthGate = new ResultGate();
  /** 드롭 장비 6부위·가방·대장간 강화(D2a·D2b) */
  private readonly gearPanel: GearPanel;
  private kGearRev = -1; private kGearVer = -1; private kGearTotals: object | undefined = undefined;

  private tab: Tab = 'equip';
  /** 단축키(N 스킬)가 가방 창을 특정 탭으로 열 때 쓴다. 창이 열리는 순간 한 번 적용하고, 8초 안에 안 열리면 버린다(느린 기기에서 명령이 다음 틱에 처리되므로 넉넉히). 가방 단추·B 키로 열면 지운다 */
  private pendingTab: { tab: Tab; until: number } | null = null;
  private readonly questBtn: HTMLButtonElement;
  private forgeTab: ForgeTab = 'town';
  private shopTab: ShopTab = 'town';
  /** 물약·비약·주문서·보석 창(D2c) */
  private readonly consumePanel: ConsumePanel;
  /** 잡화상 「젬」 탭(인앱 결제, docs/IAP-PRD.md §8) — 서버 결제 설정이 있을 때만 탭이 보인다 */
  private readonly iapPanel = new IapShopPanel();
  private kIapVer = -1;
  /** 결제 제안 띠 「보기」: 다음에 여는 잡화상 창을 젬 탭으로(8초 안에 안 열리면 버린다) */
  private iapTabUntil = 0;
  /** 레이드 지휘소 창 다시 그리기 열쇠(주 횟수·레벨·분 시계). 다른 창이면 '' */
  private kRaid = '';
  private kConsVer = -1; private kShop: ShopTab = 'town';
  /** 상점 「신규」 배지(S7): 새 마을에서 처음 여는 탭. 창을 닫을 때 본 것으로 기록한다 */
  private readonly shopSeen = new ShopSeen();
  private kSeenVer = -1; private kTown = -1;
  private sel: string | null = null;
  private narrow = false;
  private artAnim = true;
  private confirmRecover = false;
  private interactHeld = false;
  private opened = false;
  private readonly gate = new ResultGate();
  // 마지막으로 그린 값(같으면 DOM을 건드리지 않는다)
  /** 교관 창 「스킬 / 전직」 탭(C10-5). 창을 새로 열 때 스킬로 돌아간다 */
  private trainerTab: 'skills' | 'job' = 'skills'; private readonly jobPanel = new JobPanel(); private kJobKey = -1;
  private kRev = -1; private kNpc = -2; private kInv = false; private kTab: Tab = 'equip'; private kMode = ''; private kConfirm = false; private kGold = -1; private kPending = -1;
  /** 창 본문 안쪽 폭(설계 px): 가로 화면은 오른쪽 패널(600) 안, 세로 화면은 틀 폭 − 40. 상점·대장간 카드 배치(shopLayout)가 읽는다 */
  private bodyW = WIDE_BODY_W;
  private kSel: string | null = null; private kBodyW = 0; private kNarrow = false; private kForge: ForgeTab = 'town';
  /** 골드·가방 줄 top(px). 마을 퀘스트 카드 아래 끝 + 6px, 카드가 없으면 기존 자리(212*u) */
  private kHudTop = -1;
  private kHudRight = -1;
  private kTreeVer = -1; private kGrowthRev = -1; private kDot = -1; private kRebirth = -1; private kStatRev = -1; private kAvatar = -1; private kMastery = -1;
  private kPrompt: string | null = ''; private kGoldText = ''; private kScale = 0; private kW = 0; private kH = 0;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void, private readonly touchOn: boolean, private readonly input: InputSink) {
    if (!document.getElementById(STYLE_ID)) {
      const st = document.createElement('style');
      st.id = STYLE_ID;
      st.textContent = CSS;
      document.head.appendChild(st);
    }
    window.addEventListener(OPEN_IAP_TAB_EVENT, () => { this.iapTabUntil = performance.now() + 8000; });
    this.gearPanel = new GearPanel(send);
    this.consumePanel = new ConsumePanel(send);
    this.root = el('div', 'position:absolute;inset:0;pointer-events:none;display:none;', parent);
    this.root.className = 'dh-rpg';
    this.root.dataset.testid = 'rpg-root';

    // ── 마을 HUD 아이콘 줄(HUD 정리 2026-10-27 시안 A): 오른쪽 위, 전체 화면·일시정지 왼쪽. 퀘스트·가방·영웅(일일은 dailyPanel이 이 줄 왼쪽에 붙는다)
    // 골드는 왼쪽 위 체력 막대 아래 한 줄(hud.ts)로 옮겼다(이 칩은 숨김, 시험·접근성 이름용으로 남긴다)
    this.hudRow = el('div', 'position:absolute;display:flex;gap:6px;align-items:center;transform-origin:100% 0;', this.root);
    this.hudRow.className = 'dh-r-icorow';
    this.hudRow.dataset.testid = 'rpg-icon-row';
    this.goldChip = el('div', '', this.hudRow);
    this.goldChip.className = 'dh-r-hudgold';
    el('span', '', this.goldChip).className = 'dh-r-coin';
    this.goldVal = el('span', '', this.goldChip, '');
    this.goldChip.dataset.testid = 'rpg-gold';
    this.goldChip.style.display = 'none';
    this.goldChip.setAttribute('aria-label', '보유 골드');
    this.invBtn = el('button', '', this.hudRow);
    this.invBtn.type = 'button';
    this.invBtn.className = 'dh-r-bag dh-r-ico';
    this.invBtn.innerHTML = BAG_SVG;
    this.invBtn.setAttribute('aria-label', touchOn ? '가방' : '가방 (B)');
    el('span', '', this.invBtn, touchOn ? '가방' : '가방 (B)');
    this.invBtn.dataset.testid = 'rpg-open-inventory';
    this.invBtn.dataset.sfx = 'event';
    this.invBtn.setAttribute('data-tip', '가방 · 장비 (B)');
    this.invBtn.addEventListener('click', () => { this.pendingTab = null; this.send({ type: 'openInventory' }); });
    this.invDot = el('span', '', this.invBtn);
    this.invDot.className = 'dh-r-dot';
    this.invDot.hidden = true;
    this.invDot.dataset.testid = 'rpg-skill-dot';
    this.invDot.setAttribute('aria-label', '쓰지 않은 스킬 포인트');
    // 퀘스트 창 버튼(마을 전용 별도 창, G 키): 가방 옆. 왼쪽 상시 카드를 없앤 대신 여기서 열린다
    this.questBtn = el('button', '', this.hudRow);
    this.questBtn.type = 'button';
    this.questBtn.className = 'dh-r-bag dh-r-ico';
    this.questBtn.innerHTML = QUEST_SVG;
    if (!touchOn) el('span', '', this.questBtn, '퀘스트 (G)');
    this.questBtn.setAttribute('aria-label', '퀘스트 창 열기');
    this.questBtn.dataset.testid = 'rpg-open-quest';
    this.questBtn.dataset.sfx = 'event';
    this.questBtn.setAttribute('data-tip', '퀘스트 창 열기 (G)');
    this.questBtn.addEventListener('click', () => this.send({ type: 'openQuest' }));
    // 영웅 선택(c022)과 골드·가방·퀘스트 단추는 2026-10-27 오른쪽 위 아이콘 메뉴 줄(menuBar.ts)로 옮겼다.
    // 이 왼쪽 줄은 같은 단추가 두 번 보이지 않게 숨겨 둔다(요소·단축키 B는 그대로 — 가방 단추 시험·QA 호환)
    this.hudRow.style.visibility = 'hidden';

    // 상호작용 안내: 키보드는 안내 글자, 터치(와 마우스)는 같은 자리에서 누르는 대화 버튼
    this.prompt = el('button', 'position:absolute;left:50%;display:none;pointer-events:auto;cursor:pointer;touch-action:none;min-height:52px;padding:10px 26px;border-radius:26px;' +
      'border:2px solid #F2C14E;background:linear-gradient(180deg,rgba(30,40,72,.96),rgba(14,19,38,.96));color:#fff;font:800 20px ' + FONT + ';' +
      'letter-spacing:.5px;white-space:nowrap;box-shadow:0 0 0 4px rgba(9,12,26,.6),0 4px 14px rgba(0,0,0,.55),0 0 16px rgba(242,193,78,.35);transform-origin:50% 100%;', this.root);
    this.prompt.type = 'button';
    this.promptLabel = el('span', '', this.prompt);
    this.promptDot = el('span', '', this.prompt);
    this.promptDot.className = 'dh-r-dot';
    this.promptDot.hidden = true;
    this.promptDot.dataset.testid = 'rpg-prompt-dot';
    this.prompt.dataset.testid = 'rpg-prompt';
    this.prompt.dataset.sfx = 'event';
    this.prompt.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      if (this.interactHeld) return;
      this.interactHeld = true;
      try { this.prompt.setPointerCapture(e.pointerId); } catch { /* 캡처 실패는 무시 */ }
      this.input.buttonDown(Btn.Interact);
    });
    const release = (): void => this.releaseInteract();
    this.prompt.addEventListener('pointerup', release);
    this.prompt.addEventListener('pointercancel', release);
    this.prompt.addEventListener('lostpointercapture', release);

    // ── 창. z-index: 지역 안내 카드·목표 패널 같은 마을 HUD보다 위에 그린다
    this.backdrop = el('div', 'position:absolute;inset:0;z-index:40;display:none;align-items:center;justify-content:center;pointer-events:auto;touch-action:none;', this.root);
    this.backdrop.dataset.testid = 'rpg-backdrop';
    this.backdrop.addEventListener('pointerdown', (e) => { if (e.target === this.backdrop) this.send({ type: 'closeMenu' }); });
    // 창 위의 휠·터치가 뒤 게임(카메라 줌 등)으로 새지 않게 한다
    this.backdrop.addEventListener('wheel', (e) => e.stopPropagation(), { passive: true });
    this.backdrop.addEventListener('contextmenu', (e) => e.preventDefault());
    this.dialog = el('div', '', this.backdrop);
    this.dialog.className = 'dh-r-win';
    this.dialog.tabIndex = -1;
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-modal', 'true');
    this.dialog.dataset.testid = 'rpg-panel';
    // 포커스된 버튼에서 Enter·Space를 눌러도 게임 입력(상호작용·각성)으로 전달하지 않는다. 클릭 동작은 그대로다
    this.dialog.addEventListener('keydown', (e) => {
      if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); this.send({ type: 'closeMenu' }); return; }
      if (e.code === 'Tab') {
        const buttons = this.dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        const visible = Array.from(buttons).filter((b) => b.getClientRects().length > 0);
        const first = visible[0], last = visible[visible.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || document.activeElement === this.dialog)) { e.preventDefault(); first?.focus(); }
        e.stopPropagation();
      }
      if (e.code === 'Enter' || e.code === 'NumpadEnter' || e.code === 'Space') {
        if (e.repeat && (e.target as HTMLElement).tagName === 'BUTTON') e.preventDefault(); // 꾹 눌러 연속 구매 방지
        e.stopPropagation();
      }
    });

    this.stage = el('div', '', this.dialog);
    this.stage.className = 'dh-r-stage';
    this.side = el('div', 'display:contents;', this.dialog);
    const top = el('div', '', this.dialog);
    top.className = 'dh-r-top';
    const back = el('button', '', top);
    back.type = 'button';
    back.className = 'dh-r-back';
    back.innerHTML = BACK_SVG;
    back.dataset.testid = 'rpg-close';
    back.setAttribute('aria-label', '닫기');
    back.addEventListener('click', () => this.send({ type: 'closeMenu' }));
    this.title = el('div', '', top, '');
    this.title.className = 'dh-r-title';
    this.title.dataset.testid = 'rpg-title';
    if (!touchOn) el('span', '', top, 'Esc').className = 'dh-r-esc';
    this.panelGold = el('div', '', this.dialog, '');
    this.panelGold.className = 'dh-r-gold';
    this.panelGold.dataset.testid = 'rpg-panel-gold';

    const panel = el('div', '', this.dialog);
    panel.className = 'dh-r-panel';
    this.notice = el('div', '', panel);
    this.notice.className = 'dh-r-notice';
    this.notice.dataset.testid = 'rpg-notice';
    this.ph = el('div', '', panel);
    this.ph.className = 'dh-r-ph';
    this.tabs = el('div', '', panel);
    this.tabs.className = 'dh-r-tabs';
    this.tabs.setAttribute('role', 'tablist');
    this.body = el('div', '', panel);
    this.body.className = 'dh-r-body';
    this.body.dataset.testid = 'rpg-body';
    this.msg = el('div', '', panel, '');
    this.msg.className = 'dh-r-msg';
    this.msg.dataset.testid = 'rpg-msg';
    this.msg.setAttribute('role', 'status');
    this.msg.setAttribute('aria-live', 'polite');

    // B: 가방 창 열기/닫기(마을, 창이 다른 NPC 창이 아닐 때)
    window.addEventListener('keydown', this.onKey);
  }

  /** 가방 창을 열 때 처음 보일 탭을 정한다(단축키 N = 스킬). 이어서 openInventory 명령을 보내면 그 탭으로 열린다 */
  /** 가방 창이 열려 있을 때 다른 탭 단축키(K·C)를 누르면 닫지 않고 그 탭으로 바꾼다. 이미 그 탭이면 false(호출자가 닫는다) */
  switchTab(tab: 'skills' | 'status' | 'mastery'): boolean { if (this.tab === tab) return false; this.tab = tab; return true; }
  openTabNext(tab: 'skills' | 'equip' | 'sets' | 'status' | 'mastery'): void { this.pendingTab = { tab, until: performance.now() + 8000 }; }

  /** 창이 하나라도 열려 있는지 */
  get panelOpen(): boolean {
    const v = this.view.village;
    return v.menuNpc >= 0 || v.inventoryOpen;
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'KeyB' || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA')) return;
    const v = this.view;
    if (v.flow.scene !== Scene.Village || v.flow.paused) return;
    if (v.village.inventoryOpen) this.send({ type: 'closeMenu' });
    else if (v.village.menuNpc < 0) { this.pendingTab = null; this.send({ type: 'openInventory' }); }
  };

  private releaseInteract(): void {
    if (!this.interactHeld) return;
    this.interactHeld = false;
    this.input.buttonUp(Btn.Interact);
  }

  // ── 작은 부품 ──
  private mkBtn(parent: HTMLElement, label: string, onClick: () => void, o: BtnOpt = {}): HTMLButtonElement {
    const b = el('button', '', parent, label);
    b.type = 'button';
    b.className = 'dh-r-btn' + (o.held ? ' held' : o.poor && !o.disabled ? ' poor' : o.primary && !o.disabled ? ' pri' : '');
    if (o.testid) b.dataset.testid = o.testid;
    if (o.focusKey) b.dataset.focusKey = o.focusKey;
    if (o.ev) b.dataset.sfx = 'event';
    if (o.disabled || o.held) b.disabled = true;
    b.addEventListener('click', onClick);
    return b;
  }
  private chip(parent: HTMLElement, text: string, kind: 'eq' | 'own' | 'role' | 'rar'): HTMLSpanElement {
    const c = el('span', '', parent, text);
    c.className = 'dh-r-chip ' + kind;
    return c;
  }
  private heading(parent: HTMLElement, text: string): void {
    el('div', '', parent, text).className = 'dh-r-h';
  }
  private coin(parent: HTMLElement, small = true): void {
    el('span', '', parent).className = 'dh-r-coin' + (small ? ' s' : '');
  }
  /** 골드·가방 줄의 화면 사각형을 알린다(조이스틱 영역이 피한다). 바뀐 프레임에만 잰다 */
  private publishRow(on: boolean): void {
    if (!on) {
      if (villageHudLayout.row) { villageHudLayout.row = null; villageHudLayout.version++; }
      return;
    }
    if (!this.rowDirty && villageHudLayout.row) return;
    this.rowDirty = false;
    const r = viewRect(this.hudRow);
    villageHudLayout.row = r.width > 0 && r.height > 0 ? { x: r.left, y: r.top, w: r.width, h: r.height } : null;
    villageHudLayout.version++;
  }

  // ── 매 프레임(값이 바뀔 때만 DOM을 쓴다) ──
  update(u: number): void {
    const v = this.view;
    const village = v.flow.scene === Scene.Village;
    if (!village) {
      if (this.root.style.display !== 'none') {
        this.root.style.display = 'none';
        this.publishRow(false);
        this.releaseInteract();
        this.opened = false;
        setNearNpc(-1);
      }
      return;
    }
    if (this.root.style.display === 'none') this.root.style.display = 'block';
    const vv = v.village;
    const p = v.profile;
    setNearNpc(vv.nearNpc);

    // 결과 문구: seq가 바뀐 것을 한 번만 처리
    if (this.gate.poll(p.last.seq)) {
      const m = txMessage(p.last, p);
      if (m && this.panelOpen) this.setMsg(m);
    }
    // 스킬 배우기·초기화 결과: 게임이 만든 문구(growth.last.message)를 그대로 보인다(배우기 성공만 노드 이름을 붙인다)
    const gl = growthOf(v)?.last;
    if (gl && this.growthGate.poll(gl.seq) && this.panelOpen && gl.kind !== 'none' && gl.message) {
      const g = growthOf(v)!;
      const node = SKILL_NODES[gl.node];
      const ok = gl.code === 'ok' || gl.code === 'ranks_reset';
      const text = gl.kind === 'learn' && gl.code === 'ok' && node
        ? node.name + ((g.ranks[gl.node] | 0) <= 1 ? ' 습득' : ' Lv.' + (g.ranks[gl.node] | 0) + ' 강화')
        : gl.message;
      this.setMsg({ text, tone: ok ? 'ok' : 'warn' });
    }

    // 장비 명령 결과(장착·정리·강화·주문서): 강화 결과·실패 사유를 창 아래 문구 줄로(플레이 화면 팝업 없음)
    if (v.gear) this.gearPanel.pollResult(v.gear.last, (m) => { if (this.panelOpen) this.setMsg(m); });

    const open = vv.menuNpc >= 0 || vv.inventoryOpen;
    const paused = v.flow.paused;
    // HUD(창이 열려 있거나 일시정지면 숨김)
    const hudOn = !open && !paused;
    if (hudOn !== this.kHudOn) { this.kHudOn = hudOn; this.hudRow.style.display = 'none'; this.rowDirty = true; }
    const gt = goldText(p);
    if (gt !== this.kGoldText) { this.kGoldText = gt; this.goldVal.textContent = gt; this.goldChip.setAttribute('aria-label', '보유 골드 ' + gt); this.rowDirty = true; }
    const gv = growthOf(v);
    // 쓰지 않은 스킬 포인트: 가방 버튼과, 교관 세르 앞 대화 안내에 금색 점
    const unspent = hasUnspentPoints(gv) && gv!.mode === 'ok';
    const pr = hudOn ? interactPrompt(vv.nearNpc, vv.menuNpc, vv.inventoryOpen, this.touchOn, v.flow.town) : null;
    const dot = (unspent ? 1 : 0) | (unspent && pr !== null && npcDef(vv.nearNpc)?.role === 'trainer' ? 2 : 0);
    if (dot !== this.kDot) {
      this.kDot = dot;
      this.invDot.hidden = (dot & 1) === 0;
      this.promptDot.hidden = (dot & 2) === 0;
    }
    if (pr !== this.kPrompt) {
      this.kPrompt = pr;
      this.prompt.style.display = pr ? 'block' : 'none';
      if (pr) this.promptLabel.textContent = pr; else this.releaseInteract();
    }

    // 배율
    const W = viewW(), H = viewH();
    const hs = this.touchOn ? Math.max(u, 0.8) : u;
    // 오른쪽 위: 넓은 화면은 전체 화면·일시정지(94px) 왼쪽, 좁은 화면(폭 600 미만)은 맨 위 오른쪽(그 아래 줄이 전체 화면·일시정지)
    const hudTop = 6;
    const hudRight = W < 600 ? 8 : 8 + 94 + 8;
    if (hudTop !== this.kHudTop || hudRight !== this.kHudRight) { this.kHudTop = hudTop; this.kHudRight = hudRight; this.hudRow.style.top = hudTop + 'px'; this.hudRow.style.right = hudRight + 'px'; this.rowDirty = true; }
    if (u !== this.kScale || W !== this.kW || H !== this.kH) {
      this.kScale = u; this.kW = W; this.kH = H;
      this.hudRow.style.transform = 'scale(' + hs.toFixed(3) + ')';
      this.rowDirty = true;
      // 터치: 배율이 줄이는 만큼 CSS 최소 높이를 키워 보이는 높이를 44px 이상으로 유지한다
      if (this.touchOn) this.hudRow.style.setProperty('--dh-btn-min', minCssPx(hs) + 'px');
      this.prompt.style.bottom = Math.round((this.touchOn ? 24 : 96) * u) + 'px';
      this.prompt.style.transform = 'translateX(-50%) scale(' + Math.max(u, this.touchOn ? 0.85 : u).toFixed(3) + ')';
      // 가로 화면: 1280x720 설계 좌표를 화면에 맞춰 늘린 전체 층(콘셉트 배치). 세로 화면: 폭에 맞춘 틀 창
      this.narrow = W < H || W < 700;
      const ps = this.narrow ? Math.max(0.6, Math.min(1, (W - 16) / 540)) : Math.min(W / 1280, H / 720);
      this.dialog.style.zoom = String(ps);
      this.dialog.style.setProperty('--dh-btn-min', (this.touchOn ? minCssPx(ps) : 44) + 'px');
      // 마우스 모드여도 폰 크기로 줄어든 창에서 누르는 칸이 화면 기준 44px 아래로 내려가지 않게 하는 보정(터치 전용 --dh-btn-min과 별개)
      this.dialog.style.setProperty('--dh-hit-min', minCssPx(ps) + 'px');
      this.dialog.classList.toggle('narrow', this.narrow);
      this.dialog.classList.toggle('tight', !this.narrow && ps < 0.75);
      this.backdrop.style.background = this.narrow ? 'rgba(6,8,18,.72)'
        : 'linear-gradient(90deg,rgba(6,8,18,.5) 0%,rgba(6,8,18,.18) 38%,rgba(6,8,18,.32) 60%,rgba(6,8,18,.5) 100%)';
      (this.backdrop.style as unknown as Record<string, string>).backdropFilter = this.narrow ? '' : 'blur(3px) saturate(1.08)';
      if (this.narrow) {
        const dw = Math.min(620, Math.floor((W - 16) / ps));
        this.dialog.style.width = dw + 'px';
        this.dialog.style.height = Math.floor((H - 16) / ps) + 'px';
        this.bodyW = dw - 40;
      } else {
        const dw = Math.ceil(W / ps), dh = Math.ceil(H / ps);
        this.dialog.style.width = dw + 'px';
        this.dialog.style.height = dh + 'px';
        // 가방 겹층: 두 장비 열과 입상을 같은 기준 상자에(bagLayout.ts). 가운데 = 능력치 상자와 오른쪽 패널 사이
        const d = bagDollLayout(dw, dh), ds = this.dialog.style;
        ds.setProperty('--doll-l', d.left + 'px'); ds.setProperty('--doll-w', d.width + 'px'); ds.setProperty('--doll-t', d.top + 'px');
        ds.setProperty('--doll-c', d.center + 'px'); ds.setProperty('--art-h', d.artH + 'px');
        this.bodyW = WIDE_BODY_W;
      }
      if (this.dialog.classList.contains('skill-drawer')) {
        this.dialog.style.setProperty('--drawer-width', skillDrawerWidth(W, uiSizeFactor(W)) + 'px');
        this.backdrop.style.background = 'rgba(4,10,18,.08)';
        this.backdrop.style.backdropFilter = 'none';
      }
    }

    // 숨긴 줄은 조이스틱 영역이 피할 사각형이 아니다
    this.publishRow(false);

    // 창
    if (!open || paused) {
      if (this.opened || this.backdrop.style.display !== 'none') {
        const wasOpen = this.opened;
        this.backdrop.style.display = 'none';
        this.windowChanged();
        this.opened = false;
        this.confirmRecover = false;
        if (wasOpen && this.dialog.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
      }
      if (!open) { this.kNpc = -2; this.kInv = false; }
      // 창을 닫으면 이번에 본 「신규」 탭을 기록한다(보는 동안은 배지가 남는다)
      if (!open) this.shopSeen.commit();
      return;
    }
    if (!this.opened) {
      this.opened = true;
      const pt = this.pendingTab;
      this.pendingTab = null;
      this.tab = pt && pt.until > performance.now() ? pt.tab : 'equip';
      // 새 마을에서 처음 연 상점은 「마을 장비」 탭부터 보여 준다(S7 신규 품목)
      const role = npcDef(vv.menuNpc)?.role;
      // 장비 통합(ECONOMY 14절): 대장간·잡화상은 마을 장비 탭부터(보석상은 보석, 연금술사는 연금)
      this.forgeTab = role === 'jeweler' ? 'gem' : 'town';
      this.shopTab = role === 'alchemist' ? 'alchemy' : role === 'shop' && performance.now() < this.iapTabUntil && this.iapPanel.available ? 'iap' : 'town';
      this.iapTabUntil = 0;
      this.sel = null;
      this.gearPanel.open();
      this.consumePanel.open();
      this.confirmRecover = false;
      this.msg.textContent = '';
      this.backdrop.style.display = 'flex';
      this.windowChanged();
      this.kRev = -1;
      this.drawer.open();
      this.trainerTab = 'skills'; this.jobPanel.open();
    }
    if (this.backdrop.style.display === 'none') { this.backdrop.style.display = 'flex'; this.windowChanged(); this.kRev = -1; }
    if (p.revision !== this.kRev || vv.menuNpc !== this.kNpc || vv.inventoryOpen !== this.kInv || this.tab !== this.kTab || p.mode !== this.kMode ||
      this.confirmRecover !== this.kConfirm || p.gold !== this.kGold || p.pendingGold !== this.kPending || this.sel !== this.kSel || this.narrow !== this.kNarrow || this.bodyW !== this.kBodyW || this.forgeTab !== this.kForge ||
      this.drawer.version !== this.kTreeVer || slotsOf(v).revision !== this.kSlotsRev || (v.heroId ?? 'kael') !== this.kHero || (gv ? gv.revision : -1) !== this.kGrowthRev || (v.gear ? v.gear.revision : -1) !== this.kGearRev || this.gearPanel.version !== this.kGearVer || this.gearTotals !== this.kGearTotals ||
      this.consumePanel.version !== this.kConsVer || this.shopTab !== this.kShop || this.shopSeen.version !== this.kSeenVer || v.flow.town !== this.kTown || this.iapPanel.version !== this.kIapVer || this.raidKeyNow() !== this.kRaid ||
      (v.rebirth ? v.rebirth.revision : -1) !== this.kRebirth || statKey(v.statAlloc) !== this.kStatRev || (v.avatar ? v.avatar.revision : -1) !== this.kAvatar || this.masteryKeyNow() !== this.kMastery || this.jobKeyNow() !== this.kJobKey) {
      this.kJobKey = this.jobKeyNow();
      this.kMastery = this.masteryKeyNow();
      this.kRaid = this.raidKeyNow();
      this.kRebirth = v.rebirth ? v.rebirth.revision : -1; this.kStatRev = statKey(v.statAlloc); this.kAvatar = v.avatar ? v.avatar.revision : -1;
      this.kSeenVer = this.shopSeen.version; this.kTown = v.flow.town; this.kIapVer = this.iapPanel.version;
      this.kGearRev = v.gear ? v.gear.revision : -1; this.kGearVer = this.gearPanel.version; this.kGearTotals = this.gearTotals;
      this.kConsVer = this.consumePanel.version; this.kShop = this.shopTab;
      const firstDraw = this.kRev === -1;
      this.kRev = p.revision; this.kNpc = vv.menuNpc; this.kInv = vv.inventoryOpen; this.kTab = this.tab; this.kMode = p.mode;
      this.kTreeVer = this.drawer.version; this.kSlotsRev = slotsOf(v).revision; this.kHero = v.heroId ?? 'kael'; this.kGrowthRev = gv ? gv.revision : -1;
      this.kConfirm = this.confirmRecover; this.kGold = p.gold; this.kPending = p.pendingGold; this.kSel = this.sel; this.kNarrow = this.narrow; this.kBodyW = this.bodyW; this.kForge = this.forgeTab;
      this.render(firstDraw);
    }
  }

  /** 숙련 탭·대장간 초월 구역 다시 그리기 키(숙련 보기 revision + 편집 상태) */
  private masteryKeyNow(): number { const mp = this.gearPanel.masteryPanel; return mp.key(this.view) * 1000 + (mp.version % 1000); }

  /** 교관 전직 구역 다시 그리기 키(탭·전직 상태 rev·확인 단계, 매 프레임 문자열을 만들지 않게 숫자). 교관 창이 아닐 때는 -1 */
  private jobKeyNow(): number {
    const v = this.view;
    if (npcDef(v.village.menuNpc)?.role !== 'trainer' || !v.job) return -1;
    return ((v.job.rev % 1_000_000) * 1000 + (this.jobPanel.version % 1000)) * 2 + (this.trainerTab === 'job' ? 1 : 0);
  }

  private raidKeyNow(): string {
    return npcDef(this.view.village.menuNpc)?.role === 'raid' ? raidPanelKey(this.view, Date.now()) : '';
  }

  private setMsg(m: TxMessage): void {
    this.msg.textContent = m.text;
    this.msg.style.color = TONE_COLOR[m.tone];
    this.msg.dataset.tone = m.tone;
  }

  // ── 창 그리기 ──
  private render(firstDraw: boolean): void {
    // 입상·NPC 그림의 미끄러져 들어오는 등장 효과는 창을 처음 그릴 때만. 같은 창을 다시 그릴 때(장착·골드·성장 변화 등)마다
    // 효과가 처음부터 다시 돌면 그림이 왼쪽 24px에서 머문 것처럼 보인다(2026-10-27 QA: 1280×720에서 몸통 중심 −24px로 측정)
    this.artAnim = firstDraw;
    const v = this.view;
    const p = v.profile;
    const focusKey = (document.activeElement as HTMLElement | null)?.dataset?.focusKey ?? (document.activeElement as HTMLElement | null)?.dataset?.testid ?? null;
    const oldList = this.body.querySelector<HTMLElement>('.dh-sd-list');
    if (oldList) this.drawerScroll.set(oldList.dataset.scrollKey ?? '', oldList.scrollTop);
    this.body.textContent = '';
    this.tabs.textContent = ''; this.tabs.classList.remove('t7');
    this.side.textContent = '';
    this.ph.textContent = '';

    // 그 마을 사람(TOWN-SCENARIO): 같은 시설이라도 마을마다 이름·직함·인사가 다르다
    const npc = townNpcAt(v.flow.town, v.village.menuNpc) ?? npcDef(v.village.menuNpc);
    this.title.textContent = npc ? NPC_PLACE[npc.role] + ' · ' + npc.name : '가방';
    this.panelGold.textContent = '';
    this.coin(this.panelGold, false);
    el('span', '', this.panelGold, goldText(p));
    this.panelGold.setAttribute('aria-label', '보유 ' + goldText(p));

    // 프로필 모드 안내(손상이면 새로 시작 버튼, 확인 한 번 더)
    const mn = modeNotice(p.mode);
    if (mn) {
      this.notice.style.display = 'block';
      this.notice.textContent = '';
      el('div', '', this.notice, '⚠ ' + mn.text);
      if (mn.recover) {
        const row = el('div', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:8px;', this.notice);
        if (!this.confirmRecover) {
          this.mkBtn(row, '새로 시작', () => { this.confirmRecover = true; }, { testid: 'rpg-recover' });
        } else {
          el('span', 'font:800 14px ' + FONT + ';color:#ffe7a0;', row, '정말 새로 시작할까요? 지금 기록은 되돌릴 수 없어요.');
          this.mkBtn(row, '네, 새로 시작', () => { this.confirmRecover = false; this.send({ type: 'recoverProfile', requestId: makeRequestId() }); }, { primary: true, testid: 'rpg-recover-confirm', ev: true });
          this.mkBtn(row, '취소', () => { this.confirmRecover = false; }, { testid: 'rpg-recover-cancel' });
        }
      }
    } else this.notice.style.display = 'none';

    const locked = tradeLocked(p.mode);
    this.stage.textContent = '';
    // 스킬 정비만 독립 좌측 서랍을 쓴다. 가방·상점의 다른 탭은 원래 창을 유지한다.
    const trainer = npc?.role === 'trainer';
    const drawer = skillDrawerAllowed(v.flow.scene, trainer || (!npc && this.tab === 'skills'), v.flow.paused);
    const wasDrawer = this.dialog.classList.contains('skill-drawer');
    this.dialog.classList.toggle('skill-drawer', drawer);
    if (drawer) {
      this.title.textContent = '스킬';
      this.dialog.style.setProperty('--drawer-width', skillDrawerWidth(viewW(), uiSizeFactor(viewW())) + 'px');
      this.backdrop.style.background = 'rgba(4,10,18,.08)';
      this.backdrop.style.backdropFilter = 'none';
    } else if (wasDrawer) this.kScale = 0;
    this.dialog.classList.remove('wide', 'wide-trainer');
    if (drawer) {
      this.ph.style.display = this.tabs.style.display = 'none';
      // 교관 창: 전직 퀘스트가 있는 영웅이면 「스킬 / 전직」 탭(C10-5). 전직 탭도 같은 서랍 틀을 쓴다
      const jobOn = trainer && !!v.job?.available;
      if (jobOn) this.jobPanel.renderTabs(this.body, v, this.trainerTab, (t) => { this.trainerTab = t; });
      if (jobOn && this.trainerTab === 'job') { this.title.textContent = '전직'; this.jobPanel.render(this.body, v, this.send, locked); }
      else this.renderTree(locked, trainer ? TRAINER_LINE : undefined);
    } else if (npc) {
      this.ph.style.display = npc.role === 'trainer' ? 'flex' : 'none';
      this.renderNpcSide(npc.role, npc.name, npc.title, npcGreeting(v.flow.town, v.village.menuNpc, v.progress ? v.progress.cleared : 0), npc.artFilter, npc.art);
      if (npc.role === 'trainer') {
        this.tabs.style.display = 'none';
        el('b', '', this.ph, npc.title + ' · ' + npc.name);
        el('span', '', this.ph, TRAINER_LINE);
        this.renderTree(locked, TRAINER_LINE);
      } else if (npc.role === 'raid') {
        // 레이드 지휘소(RAID-PRD 1절): 탭 없이 레이드 안내·출정 본문 하나
        this.tabs.style.display = 'none';
        renderRaidPanel(this.body, v, locked, this.send);
      } else {
        this.tabs.style.display = 'flex';
        this.renderShopTabs(npc.role);
        const town = v.flow.town;
        this.gearPanel.bodyW = this.consumePanel.bodyW = this.bodyW;
        this.gearPanel.narrow = this.consumePanel.narrow = this.narrow;
        if (npc.role === 'jeweler') {
          // 보석상(마을③, S7): 대장간 보석 탭과 같은 박기·빼기·합성
          this.shopSeen.view('jeweler', 'gem', town);
          this.consumePanel.renderGems(this.body, v, p.gold, v.gear.town, locked || v.gear.mode !== 'ok');
        } else if (npc.role === 'forge') {
          if (this.forgeTab === 'enhance') this.gearPanel.renderForge(this.body, v, p.gold, v.gear.town, locked);
          else if (this.forgeTab === 'gem') this.consumePanel.renderGems(this.body, v, p.gold, v.gear.town, locked || v.gear.mode !== 'ok');
          else this.renderTownGear('forge', town, locked);
        }
        else if (this.shopTab === 'iap' && npc.role === 'shop' && this.iapPanel.available) this.iapPanel.render(this.body);
        else if (this.shopTab === 'potion') {
          this.shopSeen.view(npc.role, 'potion', town);
          this.consumePanel.renderShop(this.body, v, p.gold, v.gear.town, locked || v.gear.mode !== 'ok');
        }
        else if (this.shopTab === 'alchemy' || npc.role === 'alchemist') {
          this.shopSeen.view(npc.role, 'alchemy', town);
          this.consumePanel.renderAlchemy(this.body, v, p.gold, v.gear.town, locked || v.gear.mode !== 'ok');
        }
        else this.renderTownGear('shop', town, locked);
      }
    } else {
      this.ph.style.display = 'none';
      this.tabs.style.display = 'flex';
      this.tabs.classList.add('t7');
      for (const t of TABS) this.tabBtn(this.tabs, t, 'dh-r-tab', 'rpg-tab-' + t);
      this.renderBagSide();
      if (this.tab === 'equip') this.gearPanel.renderBag(this.body, v, locked || v.gear.mode !== 'ok');
      else if (this.tab === 'sets') renderSetPanel(this.body, v, (uid) => { this.gearPanel.select(uid); this.tab = 'equip'; });
      else if (this.tab === 'avatar') renderAvatarPanel(this.body, v, this.send, locked);
      else if (this.tab === 'items') this.consumePanel.renderItems(this.body, v, v.gear.mode !== 'ok');
      else if (this.tab === 'status') this.renderStatus();
      else if (this.tab === 'mastery') { this.gearPanel.masteryPanel.bodyW = this.bodyW; this.gearPanel.masteryPanel.render(this.body, v, locked); }
      else this.renderTree(locked);
    }

    const list = this.body.querySelector<HTMLElement>('.dh-sd-list');
    if (list) {
      const key = (v.heroId ?? 'kael') + ':' + this.drawer.selection.tab;
      list.dataset.scrollKey = key; list.scrollTop = this.drawerScroll.get(key) ?? 0;
    }

    // 키보드 포커스 복원(다시 그려도 보던 버튼에 머무른다). 처음 열 때는 창 자체에 둔다
    if (firstDraw) this.dialog.focus({ preventScroll: true });
    else if (focusKey) {
      const k = focusKey.replace(/"/g, '');
      const target = this.dialog.querySelector<HTMLElement>('[data-focus-key="' + k + '"]') ?? this.dialog.querySelector<HTMLElement>('[data-testid="' + k + '"]');
      if (target && !(target as HTMLButtonElement).disabled) target.focus({ preventScroll: true });
      else this.dialog.focus({ preventScroll: true });
    }
  }

  private tabBtn(parent: HTMLElement, t: Tab, cls: string, testid: string): void {
    const b = el('button', '', parent);
    b.type = 'button';
    b.className = cls;
    b.innerHTML = TAB_ICON[t];
    el('span', '', b, TAB_LABEL[t]);
    b.dataset.testid = testid;
    b.setAttribute('role', 'tab');
    b.setAttribute('aria-selected', this.tab === t ? 'true' : 'false');
    b.addEventListener('click', () => { this.tab = t; });
  }

  /**
   * 대장간·잡화상 탭(장비 통합, ECONOMY 14절): 대장간 = 무기(그 마을 장비)·강화·보석, 잡화상 = 방어구·장신구(그 마을 장비)·물약·연금(·젬).
   * 새 마을에서 처음 여는 탭에 「신규」 배지가 붙는다. 보석상은 보석 탭 하나, 연금술사는 연금·물약 탭
   */
  private renderShopTabs(role: NpcRole): void {
    const town = this.view.flow.town;
    const gearTown = TOWN_GEAR_TOWNS.includes(town);
    const mine = role === 'forge' || role === 'jeweler' ? this.forgeTab : this.shopTab;
    type Slot = 'enhance' | 'gem' | 'potion' | 'alchemy' | 'town' | 'iap';
    const list: Slot[] = role === 'forge' ? [...(gearTown ? ['town' as const] : []), 'enhance', 'gem']
      : role === 'jeweler' ? ['gem'] : role === 'alchemist' ? ['alchemy', 'potion'] : [...(gearTown ? ['town' as const] : []), 'potion', 'alchemy', ...(this.iapPanel.available ? ['iap' as const] : [])];
    for (const slot of list) {
      const b = el('button', '', this.tabs);
      b.type = 'button';
      b.className = 'dh-r-tab';
      b.innerHTML = slot === 'gem' ? GEM_TAB_SVG : slot === 'potion' ? POTION_TAB_SVG : slot === 'alchemy' ? ALCHEMY_TAB_SVG : slot === 'town' ? (role === 'forge' ? STAT_ICON.attack : ARMOR_SVG) : slot === 'iap' ? IAP_TAB_SVG : ENHANCE_TAB_SVG;
      el('span', '', b, slot === 'gem' ? '보석' : slot === 'potion' ? '물약' : slot === 'alchemy' ? '연금' : slot === 'town' ? (role === 'forge' ? '무기' : '방어구') : slot === 'iap' ? '젬' : '강화');
      const badgeTab: ShopBadgeTab | null = slot === 'town' || slot === 'potion' || slot === 'gem' || slot === 'alchemy' ? slot : null;
      if (badgeTab && this.shopSeen.isNew(role, badgeTab, town)) {
        // 탭 오른쪽 위 모서리에 얹는다(탭 글자 폭을 줄이지 않게 — 세로 화면 6탭에서도 글자가 줄바꿈되지 않는다)
        b.style.position = 'relative';
        const nb = el('span', 'position:absolute;top:-7px;right:-4px;padding:1px 5px;border-radius:9px;background:linear-gradient(180deg,#ff5a5a,#c81e2e);color:#fff;font:900 11px ' + FONT + ';box-shadow:0 0 6px rgba(255,80,80,.6);line-height:1.35;pointer-events:none;', b, '신규');
        nb.dataset.testid = 'rpg-shoptab-new-' + slot;
      }
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', slot === mine ? 'true' : 'false');
      b.dataset.testid = 'rpg-shoptab-' + slot;
      if (role === 'forge') b.addEventListener('click', () => { this.forgeTab = slot as ForgeTab; });
      else if (role === 'jeweler') b.addEventListener('click', () => { this.forgeTab = 'gem'; });
      else b.addEventListener('click', () => { this.shopTab = slot as ShopTab; });
    }
  }

  /** 마을 장비 탭(S7): 지금 마을 품목, 이 NPC(잡화상 방어구·장신구 / 대장간 무기) */
  private renderTownGear(role: NpcRole, town: number, locked: boolean): void {
    const v = this.view, isNew = this.shopSeen.isNew(role, 'town', town);
    this.shopSeen.view(role, 'town', town);
    const rows = townGearRows(v.gear, town, role, v.heroId ?? 'kael', v.profile.gold, locked || v.gear.mode !== 'ok');
    renderTownShop(this.body, rows, town, isNew, this.send, this.bodyW);
  }

  // ── 왼쪽 겹층 ──
  /**
   * 장착 6부위 칸(장비 통합, ECONOMY 14절). 누르면 「장비」 탭에서 그 장비를 고른다. 빈 칸은 부위 이름.
   * 가로 화면은 카엘 입상 양옆(왼쪽 무기·투구·갑옷, 오른쪽 장갑·신발·목걸이), 세로 화면은 위 미니 줄 한 줄
   */
  private gearSlots(parent: HTMLElement, cls: string): void {
    const eq = equippedCards(this.view.gear, this.view.heroId ?? 'kael');
    const box = el('div', '', parent);
    box.className = cls;
    SLOT_ORDER.forEach((slot, i) => {
      const c = eq[i];
      const cell = el('div', '', box);
      cell.className = 'dh-r-gslot';
      el('span', '', cell, SLOT_KO[slot]).className = 'lab';
      const t = this.gearPanel.tile(cell, c, 'rpg-slot-' + slot, { empty: SLOT_KO[slot], onPick: () => { this.tab = 'equip'; } });
      if (!c) t.title = SLOT_KO[slot] + ' 칸이 비었어요. 던전 드롭이나 마을 대장간·잡화상에서 구해요.';
    });
  }

  private renderBagSide(): void {
    const p = this.view.profile, s = this.view.stats;
    const rows = statRows(p, s, this.gearTotals, this.mods);
    const tier = s.armorTier;
    // 세로 화면 줄
    const m = el('div', '', this.body);
    m.className = 'dh-r-mini';
    art(hudUrl(portraitImage(tier)), 'face', m);
    const tx = el('div', '', m);
    tx.className = 'txt';
    el('b', '', tx, '카엘');
    el('span', '', tx, '공격력 ' + rows[0].total + ' · 최대 HP ' + rows[1].total);
    this.gearSlots(this.body, 'dh-r-gmini');
    // 가로 화면 겹층
    el('div', '', this.stage).className = 'dh-r-ped';
    const ka = art(kaelStandeeUrl(tier), 'dh-r-art kael', this.stage);
    ka.style.setProperty('--art-k', kaelTorsoK(tier).toFixed(4));
    if (!this.artAnim) ka.style.animation = 'none';
    // 탭은 오른쪽 판 위 한 줄만 둔다(예전 왼쪽 세로 레일과 같은 탭이 두 번 보이던 것을 정리)
    const who = el('div', '', this.side);
    who.className = 'dh-r-who';
    // 지금 영웅 이름·무기(영웅별 무기 칸 — 라이라 장비 창에 '카엘 / 대검 검사'가 나오던 것, lyra-weapon-slot QA)
    const lyraOn = this.view.heroId === 'lyra';
    el('b', '', who, lyraOn ? '라이라' : '카엘');
    el('span', '', who, lyraOn ? '장궁 궁수' : '대검 검사');
    const ap = el('span', '', who);
    ap.innerHTML = STAT_ICON.attack.replace('<svg ', '<svg style="width:16px;height:16px;vertical-align:-2px;color:#ffd76a" ');
    ap.append(' 공격력 ');
    el('em', '', ap, rows[0].total);
    this.gearSlots(this.side, 'dh-r-slots dh-r-gdoll');
    const sb = el('div', '', this.side);
    sb.className = 'dh-r-sbox';
    sb.dataset.testid = 'rpg-statbox';
    for (const r of rows) {
      const line = el('div', '', sb);
      line.className = 'dh-r-sr';
      line.innerHTML = STAT_ICON[r.key as StatKey];
      line.append(r.label);
      el('b', '', line, r.total);
    }
    const fs = skillRows(p, s, this.mods, this.gearTotals).find((x) => x.focused);
    const line = el('div', '', sb);
    line.className = 'dh-r-sr';
    line.innerHTML = STAT_ICON.cooldown;
    line.append('주력');
    el('b', 'font-family:' + FONT + ';font-size:13px;', line, fs ? fs.name : '없음');
    this.mkBtn(sb, '상세 정보', () => { this.tab = 'status'; }, { testid: 'rpg-statbox-detail' });
  }

  private renderNpcSide(role: NpcRole, name: string, title: string, greeting: string, artFilter?: string, artId?: string): void {
    // 전용 그림이 있으면 색 필터 없이 그 그림(TOWN-SCENARIO T4), 없으면 역할 기본 그림 + 색 필터
    const filter = artId ? '' : artFilter ?? NPC_ART_FILTER[role];
    // 세로 화면 줄
    const m = el('div', '', this.body);
    m.className = 'dh-r-mini';
    art(npcPortraitUrl(role, artId), 'face', m).style.filter = filter;
    const tx = el('div', '', m);
    tx.className = 'txt';
    el('b', '', tx, name + ' · ' + title);
    el('span', '', tx, '“' + greeting + '”').dataset.testid = this.narrow ? 'rpg-greeting' : 'rpg-greeting-mini';
    // 가로 화면 겹층: 상반신 그림 + 대사 상자
    const na = art(npcCutoutUrl(role, artId), 'dh-r-art npc', this.stage);
    na.style.filter = filter;
    if (!this.artAnim) na.style.animation = 'none';
    const say = el('div', '', this.side, greeting);
    say.className = 'dh-r-say';
    if (!this.narrow) say.dataset.testid = 'rpg-greeting';
    const nm = el('i', '', say, name);
    say.prepend(nm);
  }

  // ── 상태 탭 ──
  private renderStatus(): void {
    const p = this.view.profile, s = this.view.stats;
    drawStatPanel(this.body, this.view.statAlloc, this.send);
    const colors = ['#3f8fe0', '#e0485a', '#e8b04a'];
    for (const r of statRows(p, s, this.gearTotals, this.mods)) {
      const card = el('div', '', this.body);
      card.className = 'dh-r-card dh-r-stat';
      card.dataset.testid = 'rpg-stat-' + r.key;
      const ico = el('div', '', card);
      ico.className = 'dh-r-sico';
      ico.innerHTML = STAT_ICON[r.key as StatKey];
      const tv = el('div', '', card);
      el('div', '', tv, r.label).className = 'dh-r-sl';
      const tot = el('div', '', tv, r.total);
      tot.className = 'dh-r-st';
      tot.dataset.testid = 'rpg-stat-total-' + r.key;
      const right = el('div', 'min-width:0;', card);
      const sum = Math.max(0, r.parts[0]) + Math.max(0, r.parts[1]) + Math.max(0, r.parts[2]) || 1;
      const bar = el('div', '', right);
      bar.className = 'dh-r-bar';
      // 장비 통합(ECONOMY 14절): 기본 · 장비(6부위) · 성장(레벨·스킬 트리)
      const names = ['기본', '장비', '성장'];
      for (let i = 0; i < 3; i++) if (r.parts[i] > 0) el('i', 'width:' + ((r.parts[i] / sum) * 100).toFixed(2) + '%;background:linear-gradient(180deg,' + colors[i] + ',rgba(0,0,0,.25)),' + colors[i] + ';', bar, r.parts[i] / sum > 0.12 ? names[i] : '');
      const leg = el('div', '', right);
      leg.className = 'dh-r-leg';
      const parts = r.key === 'maxMp' ? [['기본', r.base]] : [['기본', r.base], ['장비', r.gear]];
      // 레벨·스킬 트리 기여분(있을 때만): 조각의 합이 합계와 같다
      if (r.parts[2] !== 0) parts.push(['성장', r.growth]);
      for (const [lab, val] of parts) { const x = el('span', '', leg, lab); el('b', '', x, val); }
    }
    const fs = skillRows(p, s, this.mods, this.gearTotals).find((x) => x.focused);
    const card = el('div', '', this.body);
    card.className = 'dh-r-card dh-r-focus';
    card.dataset.testid = 'rpg-focus-card';
    if (fs) {
      art(hudUrl(SKILL_ICON[fs.id - 1]), '', card);
      const h = el('h5', '', card, '주력 스킬 · ' + fs.name);
      h.style.color = '#fff';
      const pill = el('span', '', card);
      pill.className = 'dh-r-pill';
      pill.innerHTML = STAT_ICON.cooldown;
      pill.append('재사용 대기');
      el('b', '', pill, fs.cooldownSec);
      el('p', '', card, fs.desc);
    } else {
      el('h5', 'grid-column:1/-1;', card, '주력 스킬: 없음');
      el('p', 'grid-column:1/-1;', card, '검술 교관 세르에게서 주력 스킬을 고를 수 있어요.');
    }
  }

  /** 가방 스킬 탭과 교관 창이 같은 스킬 트리를 쓴다(교관 창은 안내 한 줄이 다르다) */
  private renderTree(locked: boolean, subtitle?: string): void {
    this.drawer.draw(this.body, { view: this.view, send: this.send, subtitle, locked },
      this.view.village.inventoryOpen ? () => { this.tab = 'equip'; } : undefined);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.releaseInteract();
    setNearNpc(-1);
    this.root.remove();
  }
}

